import { useEffect, useState } from "react";

// Sign-in rate limits (security plan C; docs/security/active/2026-11-01-auth-429-contract.md).
//
// The interceptor turns every error into a string, which loses the status and the
// seconds to wait. A request sent with `{ [AUTH_LIMIT]: true }` in its config gets an
// AuthLimitError instead when the backend refuses it for rate limiting. Only the
// sign-in pages and the refresh call send the flag, and they handle the object, so
// no other caller can ever be handed something that isn't a string.

export const AUTH_LIMIT = "authLimit";

export class AuthLimitError extends Error {
  status: number;
  code?: string;
  /** Whole seconds to wait; null when there is nothing to count down. */
  retryAfterSec: number | null;

  constructor(message: string, status: number, code: string | undefined, retryAfterSec: number | null) {
    super(message);
    this.name = "AuthLimitError";
    this.status = status;
    this.code = code;
    this.retryAfterSec = retryAfterSec;
  }

  toString() {
    return this.message;
  }
}

const seconds = (v: unknown): number | null => {
  const n = typeof v === "string" ? Number(v.trim()) : v;
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.ceil(n) : null;
};

/** Plain-language wait, minutes rounded up as the backend does. */
export const waitText = (sec: number) => {
  if (sec < 60) return sec === 1 ? "1 second" : `${sec} seconds`;
  const min = Math.ceil(sec / 60);
  return min === 1 ? "1 minute" : `${min} minutes`;
};

export const UNAVAILABLE_MESSAGE = "Sign-in is temporarily unavailable. Try again in a few minutes.";

/**
 * The rate-limit refusal in an axios error, or null for any other failure.
 * - 429 too_many_attempts or rate_limited: wait retryAfterSec (body), else the Retry-After header.
 * - any other 429 (the MFA service: too many wrong codes, resend too soon): its message, no countdown.
 * - 503 rate_limit_unavailable: the limiter's store is down; try again shortly.
 */
export const authLimitFrom = (error: any): AuthLimitError | null => {
  const res = error?.response;
  const status = res?.status;
  const body = res?.data ?? {};
  if (status === 429) {
    const code = typeof body.code === "string" ? body.code : undefined;
    const message = typeof body.message === "string" && body.message ? body.message : "Too many attempts.";
    // too_many_attempts: the auth limiters; rate_limited: the general limiter (plan A). Both carry the wait.
    if (code !== "too_many_attempts" && code !== "rate_limited") return new AuthLimitError(message, 429, code, null);
    const header = res?.headers?.["retry-after"] ?? res?.headers?.["Retry-After"];
    const sec = seconds(body.retryAfterSec) ?? seconds(header);
    return new AuthLimitError(message, 429, code, sec);
  }
  if (status === 503 && body.code === "rate_limit_unavailable") {
    return new AuthLimitError(UNAVAILABLE_MESSAGE, 503, body.code, null);
  }
  return null;
};

/**
 * Counts down a refusal's wait. `start(e)` takes what a sign-in call rejected with;
 * `message` is then live ("Try again in 14 minutes." → "… in 13 minutes."), `blocked`
 * stays true until the wait is over, and both clear on their own when it is.
 * Returns false from `start` for any error that isn't a rate-limit refusal.
 */
export const useAuthLimit = () => {
  const [until, setUntil] = useState<number | null>(null);
  const [lead, setLead] = useState("");
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (until == null) return;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= until) setUntil(null);
    }, 1000);
    return () => clearInterval(id);
  }, [until]);

  const left = until == null ? 0 : Math.max(0, Math.ceil((until - now) / 1000));

  const start = (e: unknown): boolean => {
    if (!(e instanceof AuthLimitError)) return false;
    if (e.retryAfterSec) {
      // "Too many sign-in attempts. Try again in 14 minutes." → keep the first sentence, rebuild the wait.
      const cut = e.message.search(/\s*Try again\b/i);
      setLead(cut > 0 ? e.message.slice(0, cut) : "Too many attempts.");
      const t = Date.now();
      setNow(t);
      setUntil(t + e.retryAfterSec * 1000);
    }
    return true;
  };

  return {
    start,
    blocked: left > 0,
    secondsLeft: left,
    message: left > 0 ? `${lead} Try again in ${waitText(left)}.` : null,
  };
};
